import { useState, useMemo, useCallback } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import {
  TriangleAlert,
  Search,
  ArrowLeft,
  Loader2,
  Users,
  CreditCard,
  ChevronDown,
  X,
} from "lucide-react";
import {
  useCreateCollectionJob,
  usePreviewSearch,
} from "@/features/admin/queries/useCollectionJobs";
import { useActiveCompanies } from "@/features/admin/queries";
import { CoreSignalGuideButton } from "@/features/admin/panels/core-signal/guide/CoreSignalGuideButton";
import type {
  SearchQueryFilters,
  PreviewSearchResponse,
} from "@/services/api/collection-jobs";
import type { Company } from "@/services/api/companies";

interface ImportProfilesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DialogStep = "query" | "preview";

const EMPTY_FILTERS: SearchQueryFilters = {
  companyLinkedInUrl: undefined,
  companyName: undefined,
  titleKeywords: undefined,
  descriptionKeywords: undefined,
  department: undefined,
  managementLevel: undefined,
  activeExperienceOnly: false,
  locationCountry: undefined,
  locationFull: undefined,
  createdAfter: undefined,
};

export function ImportProfilesDialog({
  open,
  onOpenChange,
}: ImportProfilesDialogProps) {
  const [step, setStep] = useState<DialogStep>("query");
  const [filters, setFilters] = useState<SearchQueryFilters>(EMPTY_FILTERS);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [companyMode, setCompanyMode] = useState<"linkedInUrl" | "companyName">(
    "linkedInUrl",
  );
  const [previewResult, setPreviewResult] =
    useState<PreviewSearchResponse | null>(null);

  const { data: companies = [] } = useActiveCompanies({ enabled: open });
  const createCollectionJob = useCreateCollectionJob();
  const previewSearch = usePreviewSearch();

  const [guideOpen, setGuideOpen] = useState(false);

  const hasFilters = useMemo(() => {
    return (
      !!filters.companyLinkedInUrl ||
      !!filters.companyName ||
      (filters.titleKeywords?.length ?? 0) > 0 ||
      (filters.descriptionKeywords?.length ?? 0) > 0 ||
      !!filters.department ||
      !!filters.managementLevel ||
      !!filters.locationCountry ||
      !!filters.locationFull
    );
  }, [filters]);

  const canPreview = hasFilters || !!selectedCompanyId;

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  );

  const updateFilter = useCallback(
    <K extends keyof SearchQueryFilters>(
      key: K,
      value: SearchQueryFilters[K],
    ) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
    if (companyId) {
      const company = companies.find((c) => c.id === companyId);
      if (company) {
        setFilters((prev) => ({
          ...prev,
          companyLinkedInUrl: company.linkedInUrl,
          companyName: undefined,
        }));
      }
    } else {
      setFilters((prev) => ({
        ...prev,
        companyLinkedInUrl: undefined,
        companyName: undefined,
      }));
    }
  };

  const handlePreview = async () => {
    try {
      const result = await previewSearch.mutateAsync({
        filters,
        itemsPerPage: 20,
      });
      setPreviewResult(result);
      setStep("preview");
    } catch {
      // Error handled by mutation onError
    }
  };

  const handleImport = async () => {
    try {
      await createCollectionJob.mutateAsync({ filters });
      resetAndClose();
    } catch {
      // Error handled by mutation onError
    }
  };

  const resetAndClose = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedCompanyId(null);
    setCompanyMode("linkedInUrl");
    setPreviewResult(null);
    setStep("query");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto">
        {step === "query" && (
          <QueryStep
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            onCompanySelect={handleCompanySelect}
            companyMode={companyMode}
            onCompanyModeChange={setCompanyMode}
            filters={filters}
            updateFilter={updateFilter}
            canPreview={canPreview}
            hasFilters={hasFilters}
            isLoading={previewSearch.isPending}
            guideOpen={guideOpen}
            setGuideOpen={setGuideOpen}
            onPreview={handlePreview}
            onCancel={resetAndClose}
          />
        )}

        {step === "preview" && previewResult && (
          <PreviewStep
            previewResult={previewResult}
            filters={filters}
            companyName={selectedCompany?.name}
            isImporting={createCollectionJob.isPending}
            onBack={() => setStep("query")}
            onImport={handleImport}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function QueryStep({
  companies,
  selectedCompanyId,
  onCompanySelect,
  companyMode,
  onCompanyModeChange,
  filters,
  updateFilter,
  canPreview,
  hasFilters,
  isLoading,
  guideOpen,
  setGuideOpen,
  onPreview,
  onCancel,
}: {
  companies: Company[];
  selectedCompanyId: number | null;
  onCompanySelect: (id: number | null) => void;
  companyMode: "linkedInUrl" | "companyName";
  onCompanyModeChange: (mode: "linkedInUrl" | "companyName") => void;
  filters: SearchQueryFilters;
  updateFilter: <K extends keyof SearchQueryFilters>(
    key: K,
    value: SearchQueryFilters[K],
  ) => void;
  canPreview: boolean;
  hasFilters: boolean;
  isLoading: boolean;
  guideOpen: boolean;
  setGuideOpen: (open: boolean) => void;
  onPreview: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Import Profiles</DialogTitle>
        <DialogDescription>
          Define your search query and preview results before importing.
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
        <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500 mt-px" />
        <p>
          Importing profiles consumes Coresignal API credits.{" "}
          <button
            type="button"
            onClick={() => setGuideOpen(true)}
            className="font-semibold underline underline-offset-2 hover:text-amber-950 transition-colors"
          >
            Read the user guide
          </button>{" "}
          before running.
        </p>
      </div>

      <CoreSignalGuideButton open={guideOpen} onOpenChange={setGuideOpen} />

      <div className="space-y-4 py-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-ink-600">
            Company
          </label>
          <div className="flex items-center gap-3 mb-2">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="company-mode"
                checked={companyMode === "linkedInUrl"}
                onChange={() => {
                  onCompanyModeChange("linkedInUrl");
                  updateFilter("companyName", undefined);
                }}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-ink-700">LinkedIn URL</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="company-mode"
                checked={companyMode === "companyName"}
                onChange={() => {
                  onCompanyModeChange("companyName");
                  onCompanySelect(null);
                  updateFilter("companyLinkedInUrl", undefined);
                }}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-ink-700">Company Name</span>
            </label>
          </div>

          {companyMode === "linkedInUrl" ? (
            <div>
              <div className="relative">
                <select
                  className="w-full appearance-none rounded-md border border-grid-200 bg-white px-3 py-2 pr-8 text-sm text-ink-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={selectedCompanyId ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    onCompanySelect(val ? Number(val) : null);
                  }}
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              </div>
              {selectedCompanyId && filters.companyLinkedInUrl && (
                <p className="mt-1 text-xs text-ink-400 truncate">
                  {filters.companyLinkedInUrl}
                </p>
              )}
            </div>
          ) : (
            <Input
              type="text"
              placeholder='e.g. "General Motors", "BMW"'
              value={filters.companyName || ""}
              onChange={(e) =>
                updateFilter("companyName", e.target.value || undefined)
              }
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-ink-700">Search Filters</h3>
            <span className="text-xs text-ink-400">(optional)</span>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Title Keywords
              </label>
              <KeywordTagInput
                values={filters.titleKeywords ?? []}
                onChange={(v) =>
                  updateFilter("titleKeywords", v.length > 0 ? v : undefined)
                }
                placeholder="Type a phrase and press Enter"
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="block text-xs font-medium text-ink-600">
                  Description Keywords
                </label>
                <KeywordMatchModeToggle
                  matchAll={filters.descriptionKeywordsMatchAll ?? false}
                  onChange={(matchAll) =>
                    updateFilter("descriptionKeywordsMatchAll", matchAll)
                  }
                />
              </div>
              <KeywordTagInput
                values={filters.descriptionKeywords ?? []}
                onChange={(v) =>
                  updateFilter(
                    "descriptionKeywords",
                    v.length > 0 ? v : undefined,
                  )
                }
                placeholder="Type a phrase and press Enter"
                separatorLabel={
                  filters.descriptionKeywordsMatchAll ? "AND" : "OR"
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Department
              </label>
              <Input
                type="text"
                placeholder='e.g. "Engineering"'
                value={filters.department || ""}
                onChange={(e) =>
                  updateFilter("department", e.target.value || undefined)
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Management Level
              </label>
              <Input
                type="text"
                placeholder='e.g. "Director", "VP"'
                value={filters.managementLevel || ""}
                onChange={(e) =>
                  updateFilter("managementLevel", e.target.value || undefined)
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Country
              </label>
              <Input
                type="text"
                placeholder='e.g. "United States"'
                value={filters.locationCountry || ""}
                onChange={(e) =>
                  updateFilter("locationCountry", e.target.value || undefined)
                }
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">
                Location
              </label>
              <Input
                type="text"
                placeholder='e.g. "Detroit, Michigan"'
                value={filters.locationFull || ""}
                onChange={(e) =>
                  updateFilter("locationFull", e.target.value || undefined)
                }
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">
              Created / Updated After
            </label>
            <Input
              type="month"
              placeholder="YYYY-MM"
              value={filters.createdAfter || ""}
              onChange={(e) =>
                updateFilter("createdAfter", e.target.value || undefined)
              }
              className="max-w-[200px]"
            />
            <p className="mt-1 text-xs text-ink-400">
              Only return profiles created or updated after this month
            </p>
          </div>

          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id="active-experience-only"
              checked={filters.activeExperienceOnly || false}
              onCheckedChange={(checked) =>
                updateFilter("activeExperienceOnly", checked === true)
              }
            />
            <label
              htmlFor="active-experience-only"
              className="text-sm text-ink-700 cursor-pointer"
            >
              Current positions only
            </label>
          </div>
        </div>

        {(canPreview || hasFilters) && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
            <QuerySummary
              companyName={
                companies.find((c) => c.id === selectedCompanyId)?.name
              }
              filters={filters}
            />
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onPreview} disabled={!canPreview || isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Preview Results (2 search credits)
        </Button>
      </DialogFooter>
    </>
  );
}

function PreviewStep({
  previewResult,
  filters,
  companyName,
  isImporting,
  onBack,
  onImport,
}: {
  previewResult: PreviewSearchResponse;
  filters: SearchQueryFilters;
  companyName?: string;
  isImporting: boolean;
  onBack: () => void;
  onImport: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Preview & Import</DialogTitle>
        <DialogDescription>
          Review the results, then import or go back to refine.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="rounded-lg border border-grid-200 bg-grid-50 px-3 py-2.5">
          <QuerySummary companyName={companyName} filters={filters} />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-ink-500" />
            <span className="font-medium text-ink-900">
              {previewResult.totalCount.toLocaleString()}
            </span>
            <span className="text-ink-500">profiles found</span>
          </div>
          {previewResult.remainingCredits != null && (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <CreditCard className="h-4 w-4" />
              <span>
                {previewResult.remainingCredits.toLocaleString()} search credits
                remaining
              </span>
            </div>
          )}
        </div>

        {previewResult.profiles.length > 0 ? (
          <div className="border border-grid-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-grid-200 bg-grid-50">
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Name
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Title
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Company
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-ink-600">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewResult.profiles.map((profile) => (
                    <tr
                      key={profile.id}
                      className="border-b border-grid-100 last:border-0"
                    >
                      <td className="px-3 py-2 text-ink-900">
                        <div className="font-medium">
                          {profile.fullName || "—"}
                        </div>
                        {profile.headline && (
                          <div
                            className="text-xs text-ink-500 truncate max-w-[200px]"
                            title={profile.headline}
                          >
                            {profile.headline}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink-700">
                        <div>{profile.activeExperienceTitle || "—"}</div>
                        {profile.activeExperienceDepartment && (
                          <div className="text-xs text-ink-500">
                            {profile.activeExperienceDepartment}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink-700">
                        {profile.companyName || "—"}
                      </td>
                      <td className="px-3 py-2 text-ink-500 text-xs">
                        {profile.locationFull || profile.locationCountry || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewResult.totalCount > previewResult.profiles.length && (
              <div className="px-3 py-2 text-xs text-ink-400 bg-grid-50 border-t border-grid-200 text-center">
                Showing {previewResult.profiles.length} of{" "}
                {previewResult.totalCount.toLocaleString()} profiles
              </div>
            )}
          </div>
        ) : (
          <div className="border border-grid-200 rounded-lg p-8 text-center text-sm text-ink-500">
            No profiles matched your query. Go back to refine your filters.
          </div>
        )}

        {previewResult.totalCount > 0 && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500 mt-px" />
            <p>
              This import will consume up to{" "}
              <strong>{previewResult.totalCount * 2} collection credits</strong>
              . Already imported profiles will be skipped automatically.
            </p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack} disabled={isImporting}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Refine Query
        </Button>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={previewResult.totalCount === 0 || isImporting}
        >
          Import {previewResult.totalCount.toLocaleString()} Profiles
        </Button>
      </DialogFooter>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription>
              Are you sure you want to import{" "}
              <strong>
                {previewResult.totalCount.toLocaleString()} profiles
              </strong>
              ? This will consume up to{" "}
              <strong>
                {(previewResult.totalCount * 2).toLocaleString()} collection
                credits
              </strong>
              .
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                onImport();
              }}
              disabled={isImporting}
            >
              {isImporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirm Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KeywordMatchModeToggle({
  matchAll,
  onChange,
}: {
  matchAll: boolean;
  onChange: (matchAll: boolean) => void;
}) {
  const options: { label: string; value: boolean; hint: string }[] = [
    {
      label: "Any",
      value: false,
      hint: "Match profiles containing any of the keywords (OR)",
    },
    {
      label: "All",
      value: true,
      hint: "Match profiles containing all of the keywords (AND)",
    },
  ];

  return (
    <div className="inline-flex items-center gap-0.5 rounded-full bg-grid-100 p-0.5">
      {options.map((opt) => {
        const active = matchAll === opt.value;
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.value)}
            title={opt.hint}
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-all ${
              active
                ? "bg-white text-imperial-500 shadow-sm"
                : "text-ink-500/60 hover:text-ink-700"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function KeywordTagInput({
  values,
  onChange,
  placeholder,
  separatorLabel = "OR",
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  separatorLabel?: string;
}) {
  const [draft, setDraft] = useState("");

  const addKeyword = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const removeKeyword = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-md border border-grid-200 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2.5 pt-2">
          {values.map((keyword, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800"
            >
              {keyword}
              {i < values.length - 1 && (
                <span className="text-blue-400 font-normal -mr-0.5 ml-0.5 text-[10px]">
                  {separatorLabel}
                </span>
              )}
              <button
                type="button"
                onClick={() => removeKeyword(i)}
                className="ml-0.5 rounded hover:bg-blue-100 p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="w-full border-0 bg-transparent px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
        placeholder={values.length > 0 ? "Add another keyword..." : placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addKeyword();
          }
          if (e.key === "Backspace" && !draft && values.length > 0) {
            removeKeyword(values.length - 1);
          }
        }}
        onBlur={addKeyword}
      />
    </div>
  );
}

function buildQuerySummary(
  companyName: string | undefined,
  filters: SearchQueryFilters,
): string {
  const parts: string[] = [];

  if (companyName) {
    parts.push(`Staff from **${companyName}**`);
  } else if (filters.companyName) {
    parts.push(`Staff from **${filters.companyName}**`);
  } else if (filters.companyLinkedInUrl) {
    parts.push(`Staff at **${filters.companyLinkedInUrl}**`);
  } else {
    parts.push("Profiles");
  }

  if (filters.titleKeywords?.length) {
    const joined = filters.titleKeywords.map((k) => `**"${k}"**`).join(" or ");
    parts.push(`with ${joined} in title`);
  }

  if (filters.descriptionKeywords?.length) {
    const joined = filters.descriptionKeywords
      .map((k) => `**"${k}"**`)
      .join(filters.descriptionKeywordsMatchAll ? " and " : " or ");
    parts.push(`with ${joined} in description`);
  }

  if (filters.department) {
    parts.push(`in **${filters.department}** department`);
  }

  if (filters.managementLevel) {
    parts.push(`at **${filters.managementLevel}** level`);
  }

  if (filters.locationFull) {
    parts.push(`located in **${filters.locationFull}**`);
  } else if (filters.locationCountry) {
    parts.push(`located in **${filters.locationCountry}**`);
  }

  if (filters.activeExperienceOnly) {
    parts.push("(current positions only)");
  }

  if (filters.createdAfter) {
    parts.push(`since **${filters.createdAfter}**`);
  }

  return parts.join(", ");
}

function QuerySummary({
  companyName,
  filters,
}: {
  companyName?: string;
  filters: SearchQueryFilters;
}) {
  const raw = buildQuerySummary(companyName, filters);

  const rendered = raw.split(/(\*\*[^*]+\*\*)/).map((segment, i) => {
    if (segment.startsWith("**") && segment.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-ink-900">
          {segment.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{segment}</span>;
  });

  return <p className="text-sm text-ink-600 leading-relaxed">{rendered}</p>;
}
